package de.oevbs.kunde.kundenportal2.authentication

import de.oevbs.kunde.kundenportal2.Pages
import de.oevbs.kunde.kundenportal2.PortalMessages
import de.oevbs.kunde.kundenportal2.authentication.SessionTimerStore.handleAndEmit
import de.oevbs.kunde.kundenportal2.components.GlobalServiceMessagesStore
import de.oevbs.kunde.kundenportal2.navigation.visit
import de.oevbs.sfa.authentication.AuthenticationParameter
import dev.fritz2.remote.Authentication
import dev.fritz2.remote.Request
import dev.fritz2.remote.http
import kotlinx.browser.window
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.datetime.Clock
import kotlinx.serialization.json.Json
import org.w3c.dom.get

object PortalSession : Authentication<PortalPrincipal>() {

    private const val SESSION_PREFIX = "de.oevbs.kupla.session"

    private val authRequest = http("/kundenportal2/auth")

    private val json = Json { ignoreUnknownKeys = true }

    /**
     * Interner Speicher für alle Callbacks, die im Falle des Logouts durch [notifyLogout] aufgerufen werden.
     *
     * Dies ist die Datenstruktur für die klassische Umsetzung des Observer-Patterns.
     */
    private val logoutObservers: MutableList<() -> Unit> = mutableListOf()

    private fun notifyLogout() {
        logoutObservers.forEach { it() }
    }

    private fun localStorageKey(id: String): String = "$SESSION_PREFIX.$id"

    private fun loadFromLocalStorage(id: String): PortalPrincipal? {
        val value = window.localStorage[localStorageKey(id)]
            ?: return null
        return json.decodeFromString(PortalPrincipal.serializer(), value)
    }

    private fun addOrUpdateLocalStorage(id: String, principal: PortalPrincipal) {
        val value = json.encodeToString(PortalPrincipal.serializer(), principal)
        window.localStorage.setItem(localStorageKey(id), value)
    }

    private fun deleteFromLocalStorage(id: String) {
        window.localStorage.removeItem(localStorageKey(id))
    }


    override fun addAuthentication(request: Request, principal: PortalPrincipal?): Request {
        return principal?.token?.let {
            request.header(AuthenticationParameter.HEADER_TOKEN, "Bearer $it")
        } ?: request
    }

    override fun authenticate() {
        // authenticate() wird aufgerufen, wenn kein Principal vorliegt. In diesem Fall muss der Nutzer sich auf der
        // Login-Seite (bei nicht angemeldetem Nutzer vor bspw. die Wurzelseite vorgeschaltet) anmelden.
        // Der LoginDataStore completed die Anmeldung dann durch einen Aufruf von PortalSession.portalLogin().
        Pages.root.visit()
    }

    private fun checkStoredData() {
        val loadedSession = loadFromLocalStorage("user")
            ?: run {
                println("Keine lokalen Anmeldedaten verfügbar. Ein manueller Login ist erforderlich.")
                return
            }

        if (loadedSession.expiresAt >= Clock.System.now()) {
            complete(loadedSession)
            SessionTimerStore.restart(Unit)
        } else {
            println("Gespeichertes Token ist abgelaufen. Ein manueller Login ist erforderlich.")
        }
    }

    fun portalLogin(portalPrincipal: PortalPrincipal, navToTop: Boolean = true) {
        if (navToTop) {
            Pages.root.visit()
        }
        SessionTimerStore.restart(Unit)
        complete(portalPrincipal)
        addOrUpdateLocalStorage(portalPrincipal._id, portalPrincipal)
    }

    fun portalLogout() {
        GlobalServiceMessagesStore.clear(Unit)
        SessionTimerStore.cancel(Unit)
        current?.let { deleteFromLocalStorage(it._id) }
        clear()
        notifyLogout()
        portalLogoutNotification(Unit)
    }

    val portalLogoutNotification = handleAndEmit { model ->
        emit(Unit)
        model
    }

    suspend fun refreshJwt(): PortalPrincipal {

        if (current == null || current?.token == null) {
            console.error("Fehler - refreshJwt wurde angefragt, Principal oder Token sind aber null.", current, current?.token)
            return PortalPrincipal()
        }

        val response = authRequest
            .use(this)
            .header(AuthenticationParameter.HEADER_REFRESH_ROLES, "true")
            .get("/info")

        if (!response.ok) {
            GlobalServiceMessagesStore.add(PortalMessages.tokenRefreshInfo)
            console.error("Fehler - refreshJwt: oe-auth-server Anfrage fehlgeschlagen: Status ${response.status}, Text: ${response.statusText}")
            return this.current ?: PortalPrincipal()
        }

        val token: String? = response.headers.get(AuthenticationParameter.HEADER_TOKEN)
        val body = response.body()

        val portalPrincipal: PortalPrincipal = json.decodeFromString(PortalPrincipal.serializer(), body)

        val newPrincipal = portalPrincipal.copy(token = token)

        portalLogin(newPrincipal, false)

        return newPrincipal
    }


    val isLoggedIn: Flow<Boolean> =
        data.map { it != null }

    fun hasAuthority(portalAuthority: PortalAuthority): Boolean =
        hasAuthority(current, portalAuthority)

    /**
     * Ermöglicht das Registrieren einer Callback-Funktion, so dass diese aufgerufen wird, sobald ein User sich
     * ausloggt. Dies sollte dazu verwendet werden, um bestimmte Aktionen wie z.B. das Resetten eines Stores
     * durchzuführen.
     */
    fun onLogout(observer: () -> Unit) {
        logoutObservers.add(observer)
    }

    init {
       checkStoredData()
    }
}